"use client";

import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Plus, Search, Power } from "lucide-react";
import { GranbayLogo } from "@/components/GranbayLogo";
import { api, clearTokens, loadTokens, saveTokens } from "@/lib/api";
import { NotificationsBell } from "@/components/social/NotificationsBell";
import { AdminInboxBell } from "@/components/AdminInboxBell";
import { MobileSidebarDrawer } from "@/components/MobileSidebarDrawer";
import { ComposerButton } from "@/components/ComposerButton";
import { useRegion } from "@/lib/regionContext";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";
import {
  formatBalanceForRegion,
  formatDualMoney,
  type MoneyCurrency,
} from "@/lib/format";
import { pickPrimaryCashWallet } from "@/lib/walletPicker";
import { useTranslation } from "react-i18next";

/**
 * SiteHeader detects auth state from localStorage so individual pages
 * don't have to know the user's auth status (closes Issue #142). The
 * old `variant` prop was the bug: every caller defaulted to `"public"`,
 * so the header always rendered Log in / Sign up even on /home.
 *
 * Authed users see a single avatar pill (initial + username + cash
 * balance) linking to /home. Polymarket / Kalshi pattern. Settings is
 * reached from the sidebar / dashboard, not as a standalone header
 * link. Logged-out users get Log in / Sign up.
 *
 * SSR renders a placeholder (avoiding a hydration mismatch with
 * client-only localStorage); after mount, the auth-dependent CTAs
 * appear in their real state. Listens to the `storage` event so
 * sign-in / sign-out in another tab updates this tab without a
 * refresh, and to a custom `granbay.balance.changed` event the
 * trade flow dispatches after order placement so the header refetches
 * without a full reload.
 */
export function SiteHeader() {
  const router = useRouter();
  const pathname = usePathname();
  const region = useRegion();
  const displayCurrency = useDisplayCurrency();
  const { t } = useTranslation();
  // 2026-05-05 — preserve the current path so post-login round-trips
  // back here instead of dumping the user on /home. Founder report
  // 2026-05-05: "I'm on /feed/posts, click Log in, sign in, get sent
  // to dashboard." The loginUrl() helper handled this for redirects
  // FROM authed-only pages (PR #1439); the chrome Login button needs
  // the same wiring.
  const headerLoginHref =
    pathname && pathname !== "/" && pathname !== "/login" && pathname !== "/signup"
      ? `/login?next=${encodeURIComponent(pathname)}`
      : "/login";
  const [username, setUsername] = useState<string | null | undefined>(
    undefined,
  );
  const [balanceCents, setBalanceCents] = useState<bigint | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const authed = username === undefined ? null : username !== null;

  // Bottom-nav hamburger (Facebook pattern, 2026-05-24) dispatches
  // granbay.sidebar.open instead of carrying its own drawer instance.
  // SiteHeader still owns the drawer mount, but the open trigger now
  // lives in MobileBottomNav.
  useEffect(() => {
    const open = () => setDrawerOpen(true);
    window.addEventListener("granbay.sidebar.open", open);
    return () => window.removeEventListener("granbay.sidebar.open", open);
  }, []);

  // 2026-05-24 — user-pill dropdown (founder direction: "on the
  // desktop version it should be under the user profile, a sub
  // menu"). Click-toggle, click-outside dismiss, Esc dismiss.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target && !target.closest("[data-user-menu]")) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [userMenuOpen]);

  // Bug-fix 2026-04-28 — the /home redesign at #320 dropped the
  // dashboard's Log out button. Re-added at the header (global,
  // works on every page) sitting next to the user pill on the
  // right edge.
  function handleLogout() {
    clearTokens();
    setUsername(null);
    setBalanceCents(null);
    router.push("/");
  }

  useEffect(() => {
    let cancelled = false;
    const compute = () => {
      const tokens = loadTokens();
      const stored = tokens?.user.username ?? null;
      // Empty-string username means the auth flow saved a stub
      // (e.g. Google OAuth before /me hydrated). Treat it as
      // "authed but unhydrated" — fetch /me to fill it in rather
      // than rendering the logged-out chrome. Without this guard,
      // a stale stub from a previous tab keeps the header showing
      // "Log in / Sign up" until the user manually refreshes.
      if (tokens && !stored) {
        api
          .me()
          .then((me) => {
            if (cancelled) return;
            const fresh = loadTokens();
            if (!fresh) return;
            saveTokens({
              ...fresh,
              user: {
                ...fresh.user,
                username: me.username,
                locale: me.locale,
                country: me.country,
              },
            });
            setUsername(me.username || null);
          })
          .catch(() => {
            if (!cancelled) setUsername(null);
          });
        return;
      }
      setUsername(stored);
    };
    compute();
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === "granbay.tokens") compute();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const [walletCurrency, setWalletCurrency] = useState<MoneyCurrency | null>(
    null,
  );

  // STORY-1805 carry-over: same fx-convert pattern the /home dashboard
  // tiles use. When the user's display_currency differs from the
  // wallet's actual denomination (e.g. BRL wallet, US flag selected),
  // convert at render time rather than slap a wrong symbol on the raw
  // cents. Falls back to wallet currency when fx is unavailable.
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;

  // Fetch the user's cash balance for the avatar pill. Refetched
  // when the trade flow dispatches `granbay.balance.changed` (window
  // event) and on tab visibility change (caught from another tab).
  //
  // After EPIC-029 STORY-1700 part 2, new signups mint USDC wallets
  // instead of BRL. Pre-existing accounts still have a BRL wallet.
  // The picker below prefers (in order): displayCurrency match, USDC,
  // then any cash wallet, so the pill shows a real balance regardless
  // of when the account was created. Crucially we ALSO surface the
  // wallet's actual currency to the formatter (walletCurrency) so the
  // pill never mis-labels (e.g. R$1,000 BRL rendered as "$1,000").
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!loadTokens()) {
        if (!cancelled) {
          setBalanceCents(null);
          setWalletCurrency(null);
        }
        return;
      }
      try {
        const wallets = await api.listMyWallets();
        const cash = pickPrimaryCashWallet(wallets, displayCurrency);
        if (!cancelled) {
          setBalanceCents(cash ? BigInt(cash.cents) : BigInt(0));
          setWalletCurrency(cash?.currency ?? null);
        }
      } catch {
        // silent — pill just won't show balance
      }
    }
    void load();
    const refresh = () => void load();
    window.addEventListener("granbay.balance.changed", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("granbay.balance.changed", refresh);
    };
  }, [authed, displayCurrency]);

  return (
    <header
      className="sticky top-0 z-50 border-b border-[var(--color-border)] backdrop-blur-md"
      style={{ background: "color-mix(in srgb, var(--color-bg) 92%, transparent)" }}
    >
      <div className="mx-auto flex h-14 max-w-[1400px] items-center gap-1.5 px-3 sm:gap-3 sm:px-5 md:gap-4">
        {/* Hamburger moved to the bottom nav 2026-05-24 (Facebook
            mobile pattern). Founder direction: cleaner top bar, all
            nav-drawer paths consolidated to the bottom tab. */}
        <Link
          href="/"
          className="-m-1 flex min-h-[44px] flex-shrink-0 items-center p-1"
          aria-label="Granbay home"
          style={{ pointerEvents: "auto" }}
        >
          {/* 2026-05-24 — full Granbay lockup on every breakpoint
              (matches v5). The icon-only variant used on phones made
              the bar feel naked; v5 showed icon + wordmark side-by-
              side and that's the reference. Inner SVG marked
              pointer-events-none below so taps bubble cleanly to the
              Link on mobile — without it iOS Safari was routing the
              tap to the SVG glyph (no handler) and the Link never
              fired (founder caught 2026-05-24). */}
          <span style={{ pointerEvents: "none" }}>
            <GranbayLogo size={36} variant="lockup" />
          </span>
        </Link>

        <form
          action="/search"
          className="relative hidden max-w-[480px] flex-1 sm:block"
        >
          <Search
            size={14}
            strokeWidth={1.5}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text3)]"
          />
          <input
            type="search"
            name="q"
            placeholder={t("header.search")}
            className="h-9 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-2 pl-9 pr-3.5 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-brand)] focus:bg-[var(--color-bg2)]"
          />
        </form>

        <div className="ml-auto flex flex-shrink-0 items-center gap-2">
          {/* Mobile-only search trigger — fires the v6 Polymarket-
              style fullscreen-from-bottom sheet (V6MobileSearchSheet)
              via granbay.mobilesearch.open. Desktop continues to use
              the CmdK palette via the inline form + keyboard shortcut.
              Founder direction 2026-05-24: bring the v6 search loop
              back. */}
          <button
            type="button"
            aria-label={t("header.searchAria")}
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent("granbay.mobilesearch.open"),
              )
            }
            className="rounded-md px-2.5 py-1.5 text-[var(--color-text2)] transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-text)] sm:hidden"
          >
            <Search size={16} strokeWidth={1.5} aria-hidden="true" />
          </button>
          <Link
            href="/markets"
            className="hidden rounded-md px-2.5 py-1.5 text-[12px] font-semibold text-[var(--color-text2)] transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-text)] md:block"
          >
            {t("header.markets")}
          </Link>
          <Link
            href="/pi/leaderboard"
            className="hidden rounded-md px-2.5 py-1.5 text-[12px] font-semibold text-[var(--color-text2)] transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-text)] md:block"
          >
            {t("header.traders")}
          </Link>
          {/* LocalePicker moved to SiteFooter on 2026-05-24
              (Polymarket-style discrete footer entry). Auto-detection
              still happens on first visit via RegionDetector (reads
              CF-IPCountry → sets granbay.region cookie); the footer
              gives the user a single canonical place to override. */}
          {/* Render auth CTAs only after hydration to avoid SSR mismatch.
              The placeholder reserves layout so the header doesn't shift. */}
          {authed === null ? (
            <div className="h-[30px] w-[140px]" aria-hidden="true" />
          ) : authed && username ? (
            <>
              {/* EPIC-047 — global "+ Post" entry point. Opens the
                  unified composer modal so every post-creation surface
                  is one component. The /posts/new long-form page is
                  retained as a permalink for ?quote= flows. */}
              <ComposerButton
                ariaLabel={t("header.newPostAria")}
                className="hidden h-9 items-center gap-1 rounded-md border border-[var(--color-border)] px-2.5 text-[12px] font-semibold text-[var(--color-text2)] transition-colors hover:border-[var(--color-border2)] hover:text-[var(--color-text)] sm:inline-flex"
              >
                <Plus
                  size={14}
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                <span>{t("header.newPost")}</span>
              </ComposerButton>
              {/* EPIC-038 — admin notification inbox. Self-gates
                  rendering on user.role === 'admin'; non-admins see
                  nothing here. Sits between locale picker and
                  user-facing notifications bell per founder spec. */}
              {/* Admin inbox is a sm:+ chrome item — on phones the
                  badge would push the auth pill off-screen. Admins
                  reach inbox via the hamburger drawer. */}
              <span className="hidden sm:inline-flex">
                <AdminInboxBell />
              </span>
              <NotificationsBell />
              {/* 2026-05-24 — desktop user pill becomes a click-toggle
                  dropdown (founder direction: "on the desktop version
                  it should be under the user profile, a sub menu").
                  Pill opens a panel with Dashboard / Settings / Sign
                  out. Click outside or Esc dismiss. Mobile users get
                  the same dropdown — extra sign-out path that doesn't
                  conflict with the bottom-nav full-page short menu. */}
              <div className="relative" data-user-menu>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                  className="flex max-w-[150px] items-center gap-1.5 overflow-hidden rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] py-1 pl-1 pr-2.5 text-[12px] font-semibold text-[var(--color-text)] transition-colors hover:border-[var(--color-border2)] sm:max-w-none sm:gap-2 sm:pr-3"
                  aria-label={
                    balanceCents !== null
                      ? `Account: ${username}, balance ${ariaBalance(
                          Number(balanceCents),
                          walletCurrency,
                          displayCurrency,
                          usdcPerBrl,
                          region.region,
                        )}`
                      : `Account: ${username}`
                  }
                >
                  <span
                    className="flex h-[24px] w-[24px] items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface2)] text-[11px] font-bold text-[var(--color-text)]"
                  >
                    {username[0]?.toUpperCase()}
                  </span>
                  {balanceCents !== null ? (
                    <DualBalance
                      cents={Number(balanceCents)}
                      walletCurrency={walletCurrency}
                      displayCurrency={displayCurrency}
                      usdcPerBrl={usdcPerBrl}
                    />
                  ) : null}
                  <span className="hidden truncate sm:inline text-[var(--color-text3)]">
                    · {username}
                  </span>
                </button>

                {userMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-50 mt-1.5 w-[220px] overflow-hidden rounded-lg border shadow-lg"
                    style={{
                      background: "var(--color-surface)",
                      borderColor: "var(--color-border)",
                      boxShadow:
                        "0 12px 32px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.06)",
                    }}
                  >
                    <Link
                      role="menuitem"
                      href="/dashboard"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-3.5 py-2.5 text-[13px] font-medium hover:bg-[var(--color-surface2)]"
                      style={{ color: "var(--color-text)" }}
                    >
                      Dashboard
                    </Link>
                    <Link
                      role="menuitem"
                      href="/dashboard/transactions"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-3.5 py-2.5 text-[13px] font-medium hover:bg-[var(--color-surface2)]"
                      style={{ color: "var(--color-text)" }}
                    >
                      Transaction history
                    </Link>
                    <div
                      aria-hidden="true"
                      className="border-t"
                      style={{ borderColor: "var(--color-border)" }}
                    />
                    {/* Money actions — moved here from the left nav
                        2026-05-24 (founder direction: account-tier
                        actions belong under the user pill, not in
                        browse-tier sidebar nav). */}
                    <Link
                      role="menuitem"
                      href="/deposit"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-3.5 py-2.5 text-[13px] font-medium hover:bg-[var(--color-surface2)]"
                      style={{ color: "var(--color-text)" }}
                    >
                      Deposit
                    </Link>
                    <Link
                      role="menuitem"
                      href="/withdraw"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-3.5 py-2.5 text-[13px] font-medium hover:bg-[var(--color-surface2)]"
                      style={{ color: "var(--color-text)" }}
                    >
                      Withdraw
                    </Link>
                    <div
                      aria-hidden="true"
                      className="border-t"
                      style={{ borderColor: "var(--color-border)" }}
                    />
                    <Link
                      role="menuitem"
                      href="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-3.5 py-2.5 text-[13px] font-medium hover:bg-[var(--color-surface2)]"
                      style={{ color: "var(--color-text)" }}
                    >
                      Settings
                    </Link>
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="block w-full px-3.5 py-2.5 text-left text-[13px] font-medium hover:bg-[var(--color-surface2)]"
                      style={{ color: "var(--color-text2)" }}
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
              {/* Bug-fix 2026-04-28 — Log out re-surfaced at the header
                  after the /home redesign dropped it. Sits next to the
                  user pill so it's always reachable from any page. */}
              {/* 2026-05-24 — logout removed from the top menu entirely
                  (mobile AND desktop) per founder direction. Sign-out
                  now lives next to the user profile on /dashboard as
                  a subtle text link. Clears 28-44px from the chrome
                  on every breakpoint. */}
            </>
          ) : (
            <>
              <Link
                href={headerLoginHref}
                className="rounded-md px-2.5 py-1.5 text-[12px] font-semibold text-[var(--color-text2)] transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]"
              >
                {t("header.logIn")}
              </Link>
              <Link
                href="/signup"
                className="rounded-md px-3.5 py-1.5 text-[12px] font-semibold text-white transition-colors"
                style={{ background: "var(--color-brand)" }}
              >
                {t("header.signUp")}
              </Link>
            </>
          )}
        </div>
      </div>
      <MobileSidebarDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </header>
  );
}

/**
 * Dual-money pill body — primary leg (wallet currency, USDC by default
 * post-STORY-1700 part 2) plus a secondary projection in the user's
 * display currency when the two differ.
 *
 * Locked display rule (founder vision 2026-05-02 PM): "Saldo: $X USDC
 * (≈ R$Y,YY hoje)". Compact mode here keeps the pill width predictable
 * across magnitudes; the full non-compact form goes into the `title`
 * tooltip + the parent's aria-label so screen-reader / hover users
 * still see the precise figure.
 *
 * No projection rendered when:
 *   - wallet currency === display currency (same-leg, no conversion)
 *   - FX rate is unavailable (`formatDualMoney` returns secondary=null)
 *   - the wallet currency is unknown yet (treated as USDC default)
 */
function DualBalance({
  cents,
  walletCurrency,
  displayCurrency,
  usdcPerBrl,
}: {
  cents: number;
  walletCurrency: MoneyCurrency | null;
  displayCurrency: MoneyCurrency;
  usdcPerBrl: number | null;
}) {
  const { t } = useTranslation();
  const region = useRegion();

  // formatBalanceForRegion handles both rules in one place:
  //   - region != brazil + displayCurrency != BRL → suppress R$, single
  //     displayCurrency primary, no secondary
  //   - otherwise → existing dual rendering (USDC / BRL stack)
  // Compact disabled: founder flagged "$80" pill when balance was
  // $79.99 — formatMoneyCompact's sub-1k branch was rounding cents
  // away. The compact form's k/M suffix kicks in at >=1k anyway, so
  // dropping `compact: true` for the pill keeps small balances
  // precise without changing the pill width meaningfully.
  const dual = formatBalanceForRegion(
    cents,
    walletCurrency,
    displayCurrency,
    usdcPerBrl,
    region.region,
  );
  const tooltip = ariaBalance(
    cents,
    walletCurrency,
    displayCurrency,
    usdcPerBrl,
    region.region,
  );
  return (
    <span
      className="min-w-0 truncate font-mono tabular-nums text-[12px]"
      title={tooltip}
    >
      {dual.primary}
      {dual.secondary !== null ? (
        <span className="ml-1 hidden text-[var(--color-text3)] sm:inline">
          (≈ {dual.secondary})
        </span>
      ) : null}
      {/* "VIRTUAL" badge removed 2026-05-24 across all regions per
       * founder direction. The closed-beta disclosure now lives only
       * in the BetaBanner banner at the top of the page; repeating it
       * on the balance pill made the chrome too wide on mobile. */}
    </span>
  );
}

/**
 * Aria-label / tooltip-friendly form of the same dual display, but
 * non-compact so the precise figure (e.g. "R$1.234,56") is read
 * verbatim by assistive tech instead of the rounded pill form.
 *
 * 2026-05-05 — region-aware. Founder report 2026-05-05 (screenshot):
 * a US-flag viewer with a legacy BRL wallet was hearing "R$… (≈ $…)"
 * read aloud. Routed through formatBalanceForRegion so non-BR users
 * never get a R$ leg in the aria string.
 */
function ariaBalance(
  cents: number,
  walletCurrency: MoneyCurrency | null,
  displayCurrency: MoneyCurrency,
  usdcPerBrl: number | null,
  region: string,
): string {
  const dual = formatBalanceForRegion(
    cents,
    walletCurrency,
    displayCurrency,
    usdcPerBrl,
    region,
  );
  return dual.secondary !== null
    ? `${dual.primary} (≈ ${dual.secondary})`
    : dual.primary;
}
